import * as THREE from 'three'

const WORLD_UP = new THREE.Vector3(0, 1, 0)
const ROTATE_SPEED = 0.005 // rad per pixel
const MIN_PHI = 0.08 // polar limit to avoid crossing the poles (gimbal)
const MAX_PHI = Math.PI - 0.08

/**
 * DCC-style camera controller (Blender/CAD):
 *
 * - Mouse wheel: dolly (zoom in/out).
 * - Left click + drag: "turntable" orbit around the point under the cursor
 *   (pivot computed by raycast against a plane at the target's depth). Rigid
 *   rotation (position + orientation) → the pivot stays fixed on screen, with NO
 *   jump and no roll (yaw about the world Y axis, pitch about the camera's
 *   horizontal right axis).
 * - Right click + drag: pan along the screen's X/Y axes.
 *
 * Replaces OrbitControls because that one always forces `lookAt(target)` and
 * therefore cannot pivot around an off-center point without recentering the view.
 */
export class CameraControls {
  enabled = true
  enableDamping = true
  dampingFactor = 0.2
  autoRotate = false
  /** Auto-rotation speed in rad/s (around the target). */
  autoRotateSpeed = 0.15
  minDistance = 1.5
  maxDistance = 40
  /** Center of the scene: pivot for auto-rotate, pan and dolly. */
  readonly target = new THREE.Vector3(0, 0, 0)

  private camera: THREE.PerspectiveCamera
  private dom: HTMLElement
  private dragButton: number | null = null
  private lastX = 0
  private lastY = 0
  private pivot = new THREE.Vector3()
  // Pending movement (with inertia/damping).
  private pendTheta = 0
  private pendPhi = 0
  private panOffset = new THREE.Vector3()

  private raycaster = new THREE.Raycaster()
  private ndc = new THREE.Vector2()
  private plane = new THREE.Plane()
  private vForward = new THREE.Vector3()
  private vRight = new THREE.Vector3()
  private vUp = new THREE.Vector3()
  private q = new THREE.Quaternion()
  private tmp = new THREE.Vector3()

  constructor(camera: THREE.PerspectiveCamera, dom: HTMLElement) {
    this.camera = camera
    this.dom = dom
    dom.addEventListener('pointerdown', this.onPointerDown)
    dom.addEventListener('wheel', this.onWheel, { passive: false })
    dom.addEventListener('contextmenu', this.onContextMenu)
  }

  // --- Events ------------------------------------------------------------
  private onPointerDown = (e: PointerEvent) => {
    if (!this.enabled) return
    this.dragButton = e.button
    this.lastX = e.clientX
    this.lastY = e.clientY
    if (e.button === 0) {
      // Pivot = point under the cursor, at the target's depth.
      this.computePivot(e.clientX, e.clientY, this.pivot)
    }
    this.dom.setPointerCapture(e.pointerId)
    window.addEventListener('pointermove', this.onPointerMove)
    window.addEventListener('pointerup', this.onPointerUp)
  }

  private onPointerMove = (e: PointerEvent) => {
    if (this.dragButton === null) return
    const dx = e.clientX - this.lastX
    const dy = e.clientY - this.lastY
    this.lastX = e.clientX
    this.lastY = e.clientY
    if (this.dragButton === 0) {
      // Orbit: accumulates rotation (applied with damping in update()).
      this.pendTheta += -dx * ROTATE_SPEED
      this.pendPhi += -dy * ROTATE_SPEED
    } else if (this.dragButton === 2) {
      this.pan(dx, dy)
    }
  }

  private onPointerUp = (e: PointerEvent) => {
    this.dragButton = null
    try {
      this.dom.releasePointerCapture(e.pointerId)
    } catch {
      /* pointer already released */
    }
    window.removeEventListener('pointermove', this.onPointerMove)
    window.removeEventListener('pointerup', this.onPointerUp)
  }

  private onWheel = (e: WheelEvent) => {
    if (!this.enabled) return
    e.preventDefault()
    // Wheel forward (deltaY<0) = zoom in; backward = zoom out.
    const factor = e.deltaY < 0 ? 0.9 : 1 / 0.9
    this.tmp.copy(this.camera.position).sub(this.target)
    let dist = this.tmp.length() * factor
    dist = THREE.MathUtils.clamp(dist, this.minDistance, this.maxDistance)
    this.tmp.setLength(dist)
    this.camera.position.copy(this.target).add(this.tmp)
  }

  private onContextMenu = (e: Event) => {
    e.preventDefault() // enables right-button dragging without a menu
  }

  // --- Computations ------------------------------------------------------
  /** 3D point under the cursor on the plane that passes through the target and faces the camera. */
  private computePivot(clientX: number, clientY: number, out: THREE.Vector3) {
    const rect = this.dom.getBoundingClientRect()
    this.ndc.set(
      ((clientX - rect.left) / rect.width) * 2 - 1,
      -((clientY - rect.top) / rect.height) * 2 + 1,
    )
    this.raycaster.setFromCamera(this.ndc, this.camera)
    this.camera.getWorldDirection(this.vForward)
    this.plane.setFromNormalAndCoplanarPoint(this.vForward, this.target)
    if (!this.raycaster.ray.intersectPlane(this.plane, out)) {
      out.copy(this.target)
    }
  }

  private pan(dx: number, dy: number) {
    const dist = this.camera.position.distanceTo(this.target)
    const fov = (this.camera.fov * Math.PI) / 180
    // Height of the target plane in world units / viewport pixels.
    const worldPerPx = (2 * Math.tan(fov / 2) * dist) / this.dom.clientHeight
    this.vRight.setFromMatrixColumn(this.camera.matrix, 0)
    this.vUp.setFromMatrixColumn(this.camera.matrix, 1)
    this.panOffset
      .addScaledVector(this.vRight, -dx * worldPerPx)
      .addScaledVector(this.vUp, dy * worldPerPx)
  }

  /** Rigid rotation (position + orientation) around a pivot. */
  private orbit(pivot: THREE.Vector3, axis: THREE.Vector3, angle: number) {
    if (angle === 0) return
    this.q.setFromAxisAngle(axis, angle)
    this.camera.position.sub(pivot).applyQuaternion(this.q).add(pivot)
    this.camera.quaternion.premultiply(this.q)
  }

  // --- Loop --------------------------------------------------------------
  update(dt: number) {
    if (!this.enabled) return
    const step = this.enableDamping ? this.dampingFactor : 1

    // Auto-rotate: constant yaw around the target (no inertia).
    if (this.autoRotate && this.dragButton === null) {
      this.orbit(this.target, WORLD_UP, this.autoRotateSpeed * dt)
    }

    // Manual orbit (with damping/inertia).
    const aTheta = this.pendTheta * step
    const aPhi = clampPitch(this.camera, this.pendPhi * step)
    if (aTheta !== 0) this.orbit(this.pivot, WORLD_UP, aTheta)
    if (aPhi !== 0) {
      // Camera's horizontal right axis (keeps the turntable roll-free).
      this.vRight.set(1, 0, 0).applyQuaternion(this.camera.quaternion)
      this.vRight.y = 0
      if (this.vRight.lengthSq() > 1e-6) this.vRight.normalize()
      this.orbit(this.pivot, this.vRight, aPhi)
    }
    this.pendTheta -= aTheta
    this.pendPhi -= this.pendPhi * step

    // Pan (with damping).
    if (this.panOffset.lengthSq() > 0) {
      this.tmp.copy(this.panOffset).multiplyScalar(step)
      this.camera.position.add(this.tmp)
      this.target.add(this.tmp)
      this.panOffset.multiplyScalar(1 - step)
    }
  }

  /** Stops the inertia (when recentering or returning from cinematic mode). */
  resetMotion() {
    this.pendTheta = 0
    this.pendPhi = 0
    this.panOffset.set(0, 0, 0)
    this.dragButton = null
  }

  dispose() {
    this.dom.removeEventListener('pointerdown', this.onPointerDown)
    this.dom.removeEventListener('wheel', this.onWheel)
    this.dom.removeEventListener('contextmenu', this.onContextMenu)
    window.removeEventListener('pointermove', this.onPointerMove)
    window.removeEventListener('pointerup', this.onPointerUp)
  }
}

/** Limits the pitch step so the camera doesn't cross the poles. */
function clampPitch(camera: THREE.PerspectiveCamera, phiStep: number): number {
  const fwd = new THREE.Vector3()
  camera.getWorldDirection(fwd)
  // Current polar angle from +Y (0 = looking up, PI = looking down).
  const phi = Math.acos(THREE.MathUtils.clamp(fwd.dot(WORLD_UP), -1, 1))
  // pitch>0 (dragging downward) raises the polar angle; clamp to the allowed range.
  const next = THREE.MathUtils.clamp(phi + phiStep, MIN_PHI, MAX_PHI)
  return next - phi
}
